import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Megaphone } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { FormattedText } from '@/components/ui/FormattedText';
import { EmptyState } from '@/components/ui/EmptyState';
import { toast } from '@/store/toastStore';
import {
  acknowledgeKbAnnouncement,
  kbQueryKeys,
  listMyKbAnnouncements,
  markAllKbAnnouncementsSeen,
} from '@/services/knowledge-base.service';

export function MyAnnouncementsPage() {
  const queryClient = useQueryClient();

  const annsQuery = useQuery({
    queryKey: kbQueryKeys.myAnnouncements(),
    queryFn: listMyKbAnnouncements,
  });

  const ackMutation = useMutation({
    mutationFn: (id: number) => acknowledgeKbAnnouncement(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kbQueryKeys.myAnnouncements() });
      toast.success('Acknowledged');
    },
  });

  const seenAllMutation = useMutation({
    mutationFn: () => markAllKbAnnouncementsSeen(),
  });

  // Single round-trip to mark every unseen announcement as seen on mount.
  // Replaces the previous per-item fan-out which produced N requests for N items.
  useEffect(() => {
    if (!annsQuery.data?.length) return;
    const hasUnseen = annsQuery.data.some((a) => !a.seenAt);
    if (hasUnseen) seenAllMutation.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annsQuery.data]);

  return (
    <div className="p-6">
      <PageHeader title="Announcements" subtitle="Important updates from your team." />

      {annsQuery.isLoading ? (
        <div className="rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-500">Loading...</div>
      ) : !annsQuery.data?.length ? (
        <EmptyState
          icon={<Megaphone className="h-8 w-8" />}
          title="No announcements"
          description="When your team publishes an announcement, it'll show up here."
        />
      ) : (
        <ul className="space-y-3">
          {annsQuery.data.map((a) => (
            <li
              key={a.kbAnnouncementId}
              className="rounded-lg border border-slate-200 bg-white p-4"
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-slate-900">{a.title}</h3>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                    {a.publishedAt && <span>{new Date(a.publishedAt).toLocaleString()}</span>}
                    {a.requiresAck && !a.acknowledgedAt && (
                      <Badge variant="warning">Acknowledgement required</Badge>
                    )}
                    {a.acknowledgedAt && <Badge variant="success">Acknowledged</Badge>}
                  </div>
                </div>
              </div>
              {a.body && (
                <FormattedText text={a.body} className="mt-3 prose prose-sm max-w-none text-slate-700" />
              )}
              <div className="mt-3 flex gap-2">
                {a.kbDocumentId && (
                  <Link to={`/knowledge/article/${a.kbDocumentId}`}>
                    <Button variant="secondary" size="sm">
                      Open document
                    </Button>
                  </Link>
                )}
                {a.requiresAck && !a.acknowledgedAt && (
                  <Button
                    size="sm"
                    leftIcon={<CheckCircle2 className="h-4 w-4" />}
                    onClick={() => ackMutation.mutate(a.kbAnnouncementId)}
                    loading={ackMutation.isPending}
                  >
                    Acknowledge
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
